import { useCallback, useRef, useState, memo } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  useNexus,
  Button,
  IconButton,
} from '@spotoninc/nexus-react';
import { Box, Flex } from '@chakra-ui/react';

import SummaryTaskContent from '../SummaryTaskContent';
import ErrorTaskContent from '../ErrorTaskContent';
import ImprovementTaskContent from '../ImprovementTaskContent';
import CompleteTaskModal from '../CompleteTaskModal';

import { useTaskDrawer } from './useTaskDrawer';
import { useTaskDrawerActions } from './useTaskDrawerActions';

import {
  ArrowLeftIcon,
  CloseIcon,
  DeleteIcon,
  ListNumberIcon,
} from '@spotoninc/nexus-web-assets';

import styles from './taskDrawer.module.css';
import '../../../../styles/reskinned-task-list.css';

/**
 * TaskDrawer component for displaying and managing task details
 * Currently shows only the header - full implementation will be added later
 *
 * Parent
 *      ImprovedTaskTable
 */
function TaskDrawer({
  selectedTask,
  isDrawerOpen,
  onClose,
  modifyTasks,
  fetchTasks,
}) {
  useNexus(); // Initialize Nexus context

  const {
    // State
    loading,
    showBackButton,
    taskDetails,
    bodyRef,

    // Functions
    handleAssignUserChange,
    onActionItemClick,
    onBackPress,
    handleClose,
    fetchTaskDetails,
    handleFeedbackSubmit,
  } = useTaskDrawer({
    isDrawerOpen,
    selectedTask,
    onClose,
    modifyTasks,
    fetchTasks,
  });
  // Get action handlers from the actions hook
  const {
    showFeedbackDrawer,
    handleOpenCompleteModal,
    handleCancelTask,
    handleInProgressTask,
    handleCompleteTask,
    handleDeleteTask,
  } = useTaskDrawerActions({
    taskDetails,
    onClose: handleClose,
    onTaskUpdate: () => {
      fetchTaskDetails();
      fetchTasks();
    }, // Refresh tasks after status update
  });

  const renderHeader = useCallback(() => {
    return (
      <Flex alignItems={'center'} gap={4} className={styles.drawerHeader}>
        {showBackButton && (
          <IconButton
            icon={ArrowLeftIcon}
            className={styles.iconButton}
            size="small"
            onClick={onBackPress}
          />
        )}
        <Box className={styles.headerText}>Task Details</Box>
        <IconButton
          icon={CloseIcon}
          className={styles.iconButton}
          size="small"
          onClick={handleClose}
        />
      </Flex>
    );
  }, [showBackButton, onBackPress, handleClose]);

  const renderFooter = useCallback(() => {
    if (!taskDetails) return null;

    const status = taskDetails.status?.toLowerCase();

    // Render buttons based on status
    if (status === 'todo' || status === 'not yet started') {
      return (
        <Flex
          alignItems={'center'}
          justifyContent={'space-between'}
          gap={4}
          className={styles.drawerFooter}
        >
          <IconButton
            variant="ghost"
            className="nexus-cancel-button"
            icon={DeleteIcon}
            onClick={handleCancelTask}
          />
          <IconButton
            variant="ghost"
            className="nexus-in-progress-button"
            icon={ListNumberIcon}
            onClick={handleInProgressTask}
          />

          <Button
            variant="filled"
            onClick={handleOpenCompleteModal}
            className="nexus-complete-button"
          >
            Complete
          </Button>
        </Flex>
      );
    }

    if (status === 'in progress' || status === 'in-progress') {
      return (
        <Flex alignItems={'center'} gap={4} className={styles.drawerFooter}>
          <Box className={styles.buttonWrapper}>
            <Button
              variant="tonal"
              onClick={handleDeleteTask}
              className={styles.button}
            >
              Cancel Task
            </Button>
          </Box>
          <Box className={styles.buttonWrapper}>
            <Button
              variant="filled"
              onClick={handleOpenCompleteModal}
              className={styles.button}
            >
              Complete
            </Button>
          </Box>
        </Flex>
      );
    }

    if (status === 'completed' || status === 'cancelled') {
      return (
        <Flex alignItems={'center'} gap={4} className={styles.drawerFooter}>
          {/* <Box className={styles.buttonWrapper}>
            <Button
              variant="tonal"
              onClick={handleDeleteTask}
              className={styles.button}
            >
              Cancel Task
            </Button>
          </Box> */}
          <Box className={styles.buttonWrapper}>
            <Button
              variant="filled"
              onClick={handleInProgressTask}
              className={styles.button}
            >
              In Progress
            </Button>
          </Box>
        </Flex>
      );
    }

    // Default fallback
    return null;
  }, [
    taskDetails,
    handleOpenCompleteModal,
    handleCancelTask,
    handleInProgressTask,
    handleDeleteTask,
  ]);

  const renderContent = useCallback(() => {
    if (!taskDetails) return null;

    if (taskDetails?.task_type === 'summary') {
      return (
        <SummaryTaskContent
          task={taskDetails}
          onActionItemClick={onActionItemClick}
          handleFeedbackSubmit={handleFeedbackSubmit}
          handleAssignUserChange={handleAssignUserChange}
        />
      );
    } else if (taskDetails?.task_type === 'error') {
      return (
        <ErrorTaskContent
          task={taskDetails}
          handleFeedbackSubmit={handleFeedbackSubmit}
          handleAssignUserChange={handleAssignUserChange}
        />
      );
    } else if (taskDetails?.task_type === 'improvement') {
      return (
        <ImprovementTaskContent
          task={taskDetails}
          handleFeedbackSubmit={handleFeedbackSubmit}
          handleAssignUserChange={handleAssignUserChange}
        />
      );
    }
    return null;
  }, [
    taskDetails,
    onActionItemClick,
    handleFeedbackSubmit,
    handleAssignUserChange,
  ]);

  if (!taskDetails) {
    return null;
  }

  return (
    <>
      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleClose}
        size="md"
        placement="right"
        className="nexus-task-drawer nexus-task-drawer-v2"
        data-testid="nexus-task-drawer"
      >
        {renderHeader()}
        <DrawerBody ref={bodyRef}>
          {/* <Box ref={bodyRef}  p={6}> */}
          {renderContent()}
          {/* </Box> */}
        </DrawerBody>
        {renderFooter()}
      </Drawer>
      <CompleteTaskModal
        isOpen={showFeedbackDrawer}
        onClose={handleCompleteTask}
        onSubmit={handleCompleteTask}
      />
    </>
  );
}

export default memo(TaskDrawer);
